import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { SignalRGroup } from "src/components";
import { useSignalR } from "src/hooks";
import { queryKeys, TaskItemDto, useGetTeamTaskItems, useGetTeamTaskItemSubtasks, type QueryParams } from "src/http";

const useGetTeamTaskItemsTree = (teamSlug: string, taskQueryParams?: QueryParams, subtaskQueryParams?: QueryParams) => {
  const { data, isLoading } = useGetTeamTaskItems(teamSlug, taskQueryParams);
  const { data: subtaskData, fetchNextPage } = useGetTeamTaskItemSubtasks(teamSlug, subtaskQueryParams);
  const [taskItems, setTaskItems] = useState<TaskItemDto[] | undefined>(undefined);
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (teamSlug !== undefined) {
      signalR.joinGroup(SignalRGroup.TeamTaskItems());
      return () => signalR.leaveGroup(SignalRGroup.TeamTaskItems());
    }
  }, [signalR, teamSlug]);

  useEffect(() => {
    const handleShouldRefetchTeamTaskItems = () => {
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
    };

    signalR.onShouldRefetchTeamTaskItems(handleShouldRefetchTeamTaskItems);
    return () => signalR.offShouldRefetchTeamTaskItems(handleShouldRefetchTeamTaskItems);
  }, [queryClient, signalR]);

  useEffect(() => {
    if (data !== undefined) {
      setTaskItems(data.taskItems);
    }
  }, [data]);

  async function fetchTaskItemSubtasks(taskItemGuid: string) {
    if (taskItems === undefined) {
      return;
    }

    let subtasks: TaskItemDto[] = [];

    // If subtasks are already cached, get them from the cache.
    if (subtaskData?.pageParams.some((pageParam) => pageParam === taskItemGuid)) {
      subtasks = getTaskItemSubtasksFromCache(taskItemGuid);
    } else {
      const fetchResult = await fetchNextPage({ pageParam: taskItemGuid });

      if (fetchResult.data?.pageParams.some((pageParam) => pageParam === taskItemGuid)) {
        const pageIndex = fetchResult.data.pageParams.indexOf(taskItemGuid);
        subtasks = fetchResult.data.pages[pageIndex].taskItems;
      }
    }

    const updatedTaskItems = produce(taskItems, (draft) => {
      const matchingTaskItems = searchTaskItems(draft, (taskItem) => taskItem.guid === taskItemGuid);
      matchingTaskItems?.forEach((taskItem) => {
        taskItem.subtasks = subtasks;
      });
    });

    setTaskItems(updatedTaskItems);
  }

  function getTaskItemSubtasksFromCache(taskItemGuid: string) {
    if (subtaskData === undefined) {
      return [];
    }

    const pageIndex = subtaskData.pageParams.indexOf(taskItemGuid);
    if (pageIndex === -1) {
      return [];
    }

    const subtasks = subtaskData.pages[pageIndex].taskItems;

    // Check to see if the subtask has any of its subtasks already loaded into the cache.
    const subtasksWithSubtasks = produce(subtasks, (draft) => {
      draft.forEach((subtask) => {
        subtask.subtasks = getTaskItemSubtasksFromCache(subtask.guid);
      });
    });

    return subtasksWithSubtasks;
  }

  return {
    data: taskItems,
    isLoading,
    fetchTaskItemSubtasks,
  };
};

const searchTaskItems = (
  taskItems: TaskItemDto[],
  predicate: (taskItem: TaskItemDto) => boolean,
): TaskItemDto[] | undefined => {
  const results: TaskItemDto[] = [];

  for (let i = 0; i < taskItems.length; i++) {
    const taskItem = taskItems[i];

    if (predicate(taskItem)) {
      results.push(taskItem);
    }

    if (taskItem.subtasks.length > 0) {
      const subtaskResults = searchTaskItems(taskItem.subtasks, predicate);

      if (subtaskResults) {
        results.push(...subtaskResults);
      }
    }
  }

  if (results.length > 0) {
    return results;
  } else {
    return undefined;
  }
};

export { useGetTeamTaskItemsTree };
